import { METER_UNITS, MODULE_RELEASES, } from './manifest.js';
import { isCoreCapability, isNavigationModule } from './capabilities.js';
import { isNeutralCommercialCode } from './commercial-codes.js';
import { manifest as platform } from './manifests/platform/module.manifest.js';
import { manifest as finance } from './manifests/finance/module.manifest.js';
import { manifest as crm } from './manifests/crm/module.manifest.js';
import { manifest as procurement } from './manifests/procurement/module.manifest.js';
import { manifest as complianceRisk } from './manifests/compliance-risk/module.manifest.js';
import { manifest as fieldService } from './manifests/field-service/module.manifest.js';
import { manifest as serviceSupport } from './manifests/service-support/module.manifest.js';
import { manifest as hr } from './manifests/hr/module.manifest.js';
import { manifest as projects } from './manifests/projects/module.manifest.js';
import { manifest as documentsContracts } from './manifests/documents-contracts/module.manifest.js';
import { manifest as lms } from './manifests/lms/module.manifest.js';
import { manifest as aiAutomation } from './manifests/ai-automation/module.manifest.js';
import { manifest as reportsAnalytics } from './manifests/reports-analytics/module.manifest.js';
/**
 * The Module Registry.
 *
 * COMMERCIAL_MODULARITY §3: "Every enforcement point resolves through the
 * registry. Nothing hardcodes a module name in a conditional." That is only
 * true if there is exactly one list, so this is it — and the imports above are
 * the only place a module key is written outside its own manifest.
 */
export const MODULE_REGISTRY = Object.freeze([
    platform,
    finance,
    crm,
    procurement,
    fieldService,
    serviceSupport,
    hr,
    projects,
    documentsContracts,
    complianceRisk,
    lms,
    aiAutomation,
    reportsAnalytics,
]);
/**
 * Platform Core is mandatory and never subject to entitlement (§1). Its key is
 * exported from here rather than restated at each enforcement point.
 */
export const PLATFORM_CORE_MODULE_KEY = 'platform';
export class InvalidManifestError extends Error {
    moduleKey;
    constructor(moduleKey, problem) {
        super(`Module manifest ${JSON.stringify(moduleKey)} is invalid: ${problem}`);
        this.name = 'InvalidManifestError';
        this.moduleKey = moduleKey;
    }
}
const MODULE_KEY_PATTERN = /^[a-z][a-z0-9-]*$/;
const FEATURE_KEY_PATTERN = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
const EDITION_CODE_PATTERN = /^ed_[0-9]{2}$/;
const METERED_EVENT_PATTERN = /^[a-z][a-z0-9_-]*\.[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
/**
 * Validates one manifest in isolation. Cross-manifest rules (dependency
 * existence, cycles, key uniqueness) are in `validateRegistry`.
 */
export function validateManifest(manifest) {
    const fail = (problem) => {
        throw new InvalidManifestError(manifest.moduleKey, problem);
    };
    if (!MODULE_KEY_PATTERN.test(manifest.moduleKey)) {
        fail('a module key is lowercase, starts with a letter and uses hyphens — it appears in ' +
            'entitlement records, audit events and invoices, and is never reused');
    }
    if (manifest.kind === 'core' && manifest.sellable) {
        fail('Platform Core is mandatory and unsellable; it is not a SKU (COMMERCIAL_MODULARITY §1)');
    }
    if (!MODULE_RELEASES.includes(manifest.commercialRelease)) {
        fail(`commercial release ${JSON.stringify(manifest.commercialRelease)} is not one of ` +
            `${MODULE_RELEASES.join(', ')}`);
    }
    if (manifest.navigationModuleKey !== undefined &&
        !isNavigationModule(manifest.navigationModuleKey)) {
        fail(`navigation module ${JSON.stringify(manifest.navigationModuleKey)} is not one of the ` +
            'approved navigation modules — navigation is design, not something a module adds to');
    }
    for (const capability of manifest.requiredCore) {
        if (!isCoreCapability(capability)) {
            fail(`required Platform Core capability ${JSON.stringify(capability)} does not exist. The ` +
                'registry rejects a dependency on something Platform Core does not provide (§3).');
        }
    }
    const featureKeys = new Set();
    for (const feature of manifest.features) {
        if (!FEATURE_KEY_PATTERN.test(feature.key)) {
            fail(`feature key ${JSON.stringify(feature.key)} is not of the form module.capability`);
        }
        if (featureKeys.has(feature.key))
            fail(`feature key ${JSON.stringify(feature.key)} is declared twice`);
        featureKeys.add(feature.key);
    }
    const editionCodes = new Set();
    for (const edition of manifest.editions) {
        if (!EDITION_CODE_PATTERN.test(edition.code)) {
            fail(`edition code ${JSON.stringify(edition.code)} must be a neutral identifier such as ` +
                "'ed_01' (C-4)");
        }
        if (!isNeutralCommercialCode(edition.code)) {
            fail(`edition code ${JSON.stringify(edition.code)} reads as a marketing name. Display names ` +
                'are catalogue data, so a rename stays a catalogue row (C-4).');
        }
        if (editionCodes.has(edition.code))
            fail(`edition ${JSON.stringify(edition.code)} is declared twice`);
        editionCodes.add(edition.code);
        for (const key of edition.features) {
            if (!featureKeys.has(key)) {
                fail(`edition ${edition.code} grants feature ${JSON.stringify(key)}, which the module does ` +
                    'not declare. A feature must exist before it can be sold (§3.2).');
            }
        }
        for (const limit of edition.limits) {
            if (!manifest.limitTypes.includes(limit)) {
                fail(`edition ${edition.code} caps limit ${limit}, which the module does not declare`);
            }
        }
    }
    // ---- metered events (owner decision S4-6) ---------------------------------
    const meteredKeys = new Set();
    for (const metered of manifest.meteredEvents) {
        if (!METERED_EVENT_PATTERN.test(metered.key)) {
            fail(`metered event ${JSON.stringify(metered.key)} is not of the form ` +
                'module.subject.verb — the shape the activity table enforces');
        }
        if (!metered.key.startsWith(`${manifest.moduleKey}.`)) {
            fail(`metered event ${JSON.stringify(metered.key)} is not prefixed with this module's key. A ` +
                "module meters its OWN activity; metering another module's is how usage ends up " +
                'attributed to the wrong SKU on an invoice.');
        }
        if (meteredKeys.has(metered.key))
            fail(`metered event ${JSON.stringify(metered.key)} is declared twice`);
        meteredKeys.add(metered.key);
        if (!METER_UNITS.includes(metered.unit)) {
            fail(`metered event ${JSON.stringify(metered.key)} has unit ${JSON.stringify(metered.unit)}`);
        }
        if (metered.countsToward !== undefined && !manifest.limitTypes.includes(metered.countsToward)) {
            fail(`metered event ${JSON.stringify(metered.key)} counts toward limit ` +
                `${metered.countsToward}, which the module does not declare. A cap nobody declared is ` +
                'a cap nobody can enforce.');
        }
        // C-3: the active-user metric is a count of distinct people. A declaration
        // that feeds `active_user` with anything but the `active_user` unit would
        // count actions and call them people.
        if (metered.countsToward === 'active_user' && metered.unit !== 'active_user') {
            fail(`metered event ${JSON.stringify(metered.key)} counts toward the active_user limit but is ` +
                `measured in ${metered.unit}. The active-user metric is a count of distinct people ` +
                'and is not derivable from a total (C-3).');
        }
    }
    if (manifest.sellable && manifest.editions.length === 0) {
        fail('a sellable module has at least one edition; there would otherwise be nothing to sell');
    }
    if (manifest.optionalModules.includes(manifest.moduleKey)) {
        fail('a module cannot be its own optional dependency');
    }
}
/**
 * Validates the registry as a whole.
 *
 * The cross-manifest rules are the ones a single manifest cannot see: a
 * dependency on a module that does not exist, a dependency cycle, a feature key
 * claimed by two modules, a duplicated module key.
 */
export function validateRegistry(manifests = MODULE_REGISTRY) {
    for (const manifest of manifests)
        validateManifest(manifest);
    const byKey = new Map();
    for (const manifest of manifests) {
        if (byKey.has(manifest.moduleKey)) {
            throw new InvalidManifestError(manifest.moduleKey, 'the module key is declared twice');
        }
        byKey.set(manifest.moduleKey, manifest);
    }
    // Deliberately NOT checked: a metric key claimed by two modules. Module keys
    // are unique (above) and every metered event must be prefixed with its own
    // module's key, so global uniqueness is already implied. A second check here
    // could never fire — and an unreachable branch that reads like a control is
    // worse than no branch, because the next person believes it.
    //
    // Feature keys DO need the check below: they are of the form
    // `module.capability` by convention but nothing ties the prefix to the
    // declaring module, so two modules genuinely can claim one.
    const featureOwner = new Map();
    for (const manifest of manifests) {
        for (const feature of manifest.features) {
            const owner = featureOwner.get(feature.key);
            if (owner !== undefined) {
                throw new InvalidManifestError(manifest.moduleKey, `feature key ${JSON.stringify(feature.key)} is already declared by ${owner}`);
            }
            featureOwner.set(feature.key, manifest.moduleKey);
        }
    }
    for (const manifest of manifests) {
        for (const dependency of manifest.optionalModules) {
            const target = byKey.get(dependency);
            if (target === undefined) {
                throw new InvalidManifestError(manifest.moduleKey, `optional dependency ${JSON.stringify(dependency)} is not a registered module`);
            }
            if (!target.sellable) {
                throw new InvalidManifestError(manifest.moduleKey, `optional dependency ${JSON.stringify(dependency)} is not sellable. A dependency on ` +
                    'mandatory Core is a requiredCore declaration, not an optional module (§5).');
            }
        }
    }
    // NO HIDDEN COUPLING (§5). "If a module cannot function without another
    // sellable module, that is a bundling decision, not an optional dependency."
    // `requiredCore` can only name a Platform Core capability — which is
    // mandatory for every tenant and so can never be unmet — so the structural
    // guarantee is already made by `isCoreCapability` above. What is checked here
    // is the one thing that could undermine it: a module key that collides with a
    // capability name, which would make `requiredCore: ['inventory']` ambiguous
    // between the Core context and a hypothetical SKU of the same name.
    for (const manifest of manifests) {
        if (manifest.sellable && isCoreCapability(manifest.moduleKey)) {
            throw new InvalidManifestError(manifest.moduleKey, 'a sellable module key must not collide with a Platform Core capability name — a ' +
                'requiredCore declaration would then be ambiguous between the mandatory Core ' +
                'context and a SKU the customer might not have bought (§5).');
        }
    }
    // Deliberately NOT checked: cycles among optional dependencies. Finance
    // enriches Field Service (job-to-invoice) and Field Service enriches Finance,
    // and §5 requires both Finance-without-Field-Service and
    // Field-Service-without-Finance to be coherent products. An optional
    // dependency is satisfiable by absence, so a mutual pair is the normal case
    // rather than a defect — the rule that matters is the one above, which keeps
    // "cannot function without" out of the optional list entirely.
}
/**
 * The metered event with this key, if any module declares it (S4-6).
 *
 * This is the lookup metering calls before it records anything, which is what
 * makes "the registry rejects an undeclared metered event" a property of the
 * running system rather than of a review.
 */
export function meteredEvent(key) {
    return METERED_BY_KEY.get(key);
}
/** Which module owns a metric key, so usage is attributed to the right SKU. */
export function moduleForMeteredEvent(key) {
    return METERED_MODULE_BY_KEY.get(key);
}
export function meteredEventsOf(moduleKey) {
    return MODULE_REGISTRY.find((manifest) => manifest.moduleKey === moduleKey)?.meteredEvents ?? [];
}
const METERED_BY_KEY = new Map(MODULE_REGISTRY.flatMap((manifest) => manifest.meteredEvents.map((metered) => [metered.key, metered])));
const METERED_MODULE_BY_KEY = new Map(MODULE_REGISTRY.flatMap((manifest) => manifest.meteredEvents.map((metered) => [metered.key, manifest.moduleKey])));
// Validated at load: an invalid registry cannot reach a running system, and a
// manifest that would not survive review does not survive `import`.
validateRegistry();
const BY_KEY = new Map(MODULE_REGISTRY.map((manifest) => [manifest.moduleKey, manifest]));
const FEATURE_OWNER = new Map(MODULE_REGISTRY.flatMap((manifest) => manifest.features.map((feature) => [feature.key, manifest.moduleKey])));
export function moduleDefinition(moduleKey) {
    return BY_KEY.get(moduleKey);
}
export function isRegisteredModule(moduleKey) {
    return BY_KEY.has(moduleKey);
}
/** Which module owns a feature key — the module that must itself be entitled. */
export function featureOwner(featureKey) {
    return FEATURE_OWNER.get(featureKey);
}
export function isRegisteredFeature(featureKey) {
    return FEATURE_OWNER.has(featureKey);
}
/**
 * Every module a customer can actually buy.
 *
 * DELIBERATELY NOT FILTERED BY RELEASE. This is the list the structural rules
 * are stated against — an optional dependency must name a sellable module (§5),
 * and Finance names Field Service, which is not a Phase 1 module. Narrowing
 * this would turn a commercial decision into a validation failure. What is
 * offered *now* is `phaseOneModules()` below.
 */
export function sellableModules() {
    return MODULE_REGISTRY.filter((manifest) => manifest.sellable);
}
/**
 * Every module the approved Phase 1 commercial model offers.
 *
 * The list navigation is composed from and the list a grant is checked
 * against. Platform Core is not here: it is mandatory, never granted, and
 * reaches navigation through `PLATFORM_CORE_MODULE_KEY` instead.
 */
export function phaseOneModules() {
    return MODULE_REGISTRY.filter((manifest) => manifest.sellable && manifest.commercialRelease === 'phase_1');
}
/** Is this module offered in the approved Phase 1 commercial model? */
export function isPhaseOneModule(moduleKey) {
    return moduleDefinition(moduleKey)?.commercialRelease === 'phase_1';
}
/** The release a module belongs to, or undefined if it is not registered. */
export function commercialRelease(moduleKey) {
    return moduleDefinition(moduleKey)?.commercialRelease;
}
/** The features an edition grants, resolved from the registry. */
export function editionFeatures(moduleKey, editionCode) {
    const edition = moduleDefinition(moduleKey)?.editions.find((value) => value.code === editionCode);
    return edition?.features ?? [];
}
export function isRegisteredEdition(moduleKey, editionCode) {
    return moduleDefinition(moduleKey)?.editions.some((value) => value.code === editionCode) ?? false;
}
//# sourceMappingURL=registry.js.map